// 📄 pages/MiEspacio/sections/BusquedaIASection.tsx
//
// "Búsqueda inteligente con IA" (2026-08-20, Fase 4) — pregunta en
// lenguaje natural sobre las Notas de SAP (propias + compartidas por el
// equipo del/los proyecto(s) en `contextosConsulta`) y los Casos de
// Prueba de esos mismos proyectos. Reusa el MISMO selector de "Contexto"
// que ya tiene Mi Espacio (Home) — no hay un picker nuevo acá, el alcance
// de la búsqueda es siempre "lo que estás viendo" en las otras pestañas.
import { useState } from 'react';
import { Sparkles, Search, RefreshCw, Lock } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import { armarContextoBusqueda, preguntarBusquedaInteligente } from '../../../services/busquedaInteligenteService.ts';
import { MiEspacioSeccionProps } from '../miEspacioContexto.ts';

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";

export default function BusquedaIASection({ contextosConsulta, proyectosPorId }: MiEspacioSeccionProps) {
  const { user, profile } = useAuth();
  const [pregunta, setPregunta] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [respuesta, setRespuesta] = useState<{ text: string; generatedBy: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // "Planes/Suscripciones" (Fase 5/5, 2026-08-26) — gate real ya vive en
  // la Cloud Function (busquedaInteligente.js, valida contra costo real de
  // API); esto es solo para no dejar que el usuario escriba una pregunta
  // y se lleve un error genérico — le explicamos por qué de entrada. Va
  // DESPUÉS de los hooks (nunca antes de un return condicional) para no
  // romper el orden de hooks entre renders.
  if (!profile?.features?.busquedaIA) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
        <Lock className="w-8 h-8 mx-auto mb-3 text-gray-600" />
        <h2 className="text-sm font-semibold text-gray-300 mb-1">Búsqueda con IA no incluida en tu plan</h2>
        <p className="text-xs text-gray-500 max-w-sm mx-auto">
          Pedile a un Admin de la plataforma que te asigne un plan Pro o Enterprise desde Organizaciones para habilitar esta función.
        </p>
      </div>
    );
  }

  const handleBuscar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !pregunta.trim() || buscando) return;
    setBuscando(true);
    setError(null);
    setRespuesta(null);
    try {
      const contexto = await armarContextoBusqueda({ uid: user.uid, contextosConsulta, proyectosPorId });
      const result = await preguntarBusquedaInteligente(pregunta.trim(), contexto);
      setRespuesta(result);
    } catch (err: any) {
      console.error('Error en búsqueda inteligente:', err);
      setError(err.message || 'No se pudo generar una respuesta. Intentá de nuevo.');
    } finally {
      setBuscando(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-purple-400" />
          <h2 className="text-sm font-semibold text-gray-300">Preguntale a la IA</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Busca en tus Notas de SAP (propias y compartidas por el equipo) y en los Casos de Prueba de este contexto — ideal para "¿ya resolvimos este error antes?".
        </p>

        <form onSubmit={handleBuscar} className="flex gap-2">
          <input
            className={inputClass}
            value={pregunta}
            onChange={(e) => setPregunta(e.target.value)}
            placeholder='Ej: "¿Ya tuvimos un error de timeout en la creación de órdenes?"'
          />
          <button
            type="submit"
            disabled={buscando || !pregunta.trim()}
            className="flex-shrink-0 flex items-center gap-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:opacity-50 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all"
          >
            {buscando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>
        </form>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm">{error}</div>
      )}

      {respuesta && (
        <div className="bg-white/5 border border-purple-500/20 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <p className="text-xs font-semibold text-purple-300 uppercase tracking-wider">Respuesta</p>
          </div>
          <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">{respuesta.text}</p>
        </div>
      )}
    </div>
  );
}
